/**
 * The single scroll motion behind the deck editor's focus placement.
 *
 * ADR-067 Decision A requires one eased motion per focus, with no visible
 * restart between growing a field and settling its position. The arithmetic of
 * WHERE to land is `deckFocusPositioning.ts`; this module owns HOW the page
 * gets there, and exists because the two are not independent: the placement's
 * target is computed from geometry that is still moving when the motion starts
 * (the keyboard is still animating in, the focused field has not finished
 * growing), so a better-informed target arrives mid-flight and must be honoured.
 *
 * Native `scrollTo({ behavior: 'smooth' })` cannot honour it. A second call
 * cancels the running animation and restarts from wherever it stood, and an
 * instant write during the window truncates it outright — which is the confirmed
 * cause of the "walking" placement recorded in
 * docs/diagnostics/deck-focus-placement-partial-delta-2026-07-31.md §3.5/§3.6
 * and measured on device: two writes per focus, a smooth one immediately
 * superseded by an instant caret-keeping one, delivering a few percent of a
 * large delta and the whole of a small one.
 *
 * So the ease is driven here instead, over a MUTABLE target. Retargeting keeps
 * the same motion running and re-aims it from the position it currently
 * occupies; nothing is cancelled, nothing restarts, and the last computation —
 * the one made against settled geometry — is the one that decides where the
 * page comes to rest. There is exactly one writer to the scroller per focus
 * gesture, and it is this loop.
 *
 * What this is NOT, deliberately: it does not wrap, shadow or replace any scroll
 * primitive (the mechanism that hung the editor in the rejected attempt e4c8296,
 * named in §9.4), and it holds no lock — a user gesture aborts it outright, per
 * the assistance principle of ADR-067's 2026-07-30 amendment.
 */

/** How long a first placement takes. Long enough to read as motion rather than
 * a jump, short enough that the geometry it was computed against is still
 * current when it lands. */
export const PLACEMENT_DURATION = 260

/** Floor on the time a retarget is given. A correction arriving very late in the
 * ease would otherwise finish in a couple of frames, which reads as the jerk the
 * whole mechanism exists to remove. */
export const RETARGET_MIN_DURATION = 120

/** Below this, a target the scroller already occupies is not worth a motion.
 * Applied ONLY when nothing is in flight: a motion already travelling towards a
 * stale target must be re-aimed however small the correction, or the deadband
 * would leave a truncated earlier motion short of the target it should have had
 * (the failure in probe block #5). */
export const DEADBAND = 2

/** Treated as arrived. `scrollTop` is a float and device-pixel rounding makes
 * exact equality unreachable. */
export const REACH_EPSILON = 1

/** How long past the ease the loop keeps trying when the target is beyond the
 * scroller's current maximum — the window in which a reserve that was grown in
 * the same pass gets committed and rendered. Also the hard cap on the loop, so
 * an unreachable target (clamped at 0, or content that never grows) ends the
 * motion instead of spinning. */
export const RESERVE_GRACE = 300

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3

export interface ScrollMotion {
  /**
   * Aim the motion at an absolute `scrollTop`. Starts it when idle, re-aims it
   * in place when already running. `onSettled` replaces any previously pending
   * one and fires once, when the page has come to rest at the final target.
   */
  to(target: number, duration: number, onSettled?: () => void): void
  inFlight(): boolean
  /** Give up the motion where it stands, without settling. */
  abort(): void
}

interface MotionEnv {
  now?: () => number
  raf?: (cb: () => void) => number
  caf?: (id: number) => void
}

export function createScrollMotion(el: HTMLElement, env: MotionEnv = {}): ScrollMotion {
  const now = env.now ?? (() => performance.now())
  const raf = env.raf ?? ((cb: () => void) => requestAnimationFrame(cb))
  const caf = env.caf ?? ((id: number) => cancelAnimationFrame(id))

  let active = false
  let frame: number | null = null
  let from = 0
  let target = 0
  let startedAt = 0
  let duration = 0
  let settled: (() => void) | null = null

  const maxScroll = () => Math.max(0, el.scrollHeight - el.clientHeight)

  const finish = () => {
    active = false
    frame = null
    const done = settled
    settled = null
    done?.()
  }

  const tick = () => {
    frame = null
    if (!active) return
    const elapsed = now() - startedAt
    const t = duration <= 0 ? 1 : Math.min(1, elapsed / duration)

    // The write is clamped to what the scroller can currently reach rather than
    // skipped, so a target that is momentarily out of range (the reserve grown
    // in this same pass has not rendered yet) still moves the page as far as it
    // can this frame and covers the rest as soon as the padding lands.
    const wanted = from + (target - from) * easeOutCubic(t)
    el.scrollTop = Math.min(Math.max(wanted, 0), maxScroll())

    if (t < 1) { frame = raf(tick); return }

    // Past the ease. Arrival is asserted against the scroller's own position,
    // not against the clock — a motion is not finished merely because its time
    // is up. The grace bounds the wait, so an unreachable target ends here.
    const reachable = Math.min(Math.max(target, 0), maxScroll())
    const arrived = Math.abs(el.scrollTop - reachable) <= REACH_EPSILON
    if ((arrived && target <= maxScroll() + REACH_EPSILON) || elapsed >= duration + RESERVE_GRACE) {
      finish()
      return
    }
    frame = raf(tick)
  }

  return {
    inFlight: () => active,

    to(next: number, nextDuration: number, onSettled?: () => void) {
      // Already there, and nothing running to correct: the placement is made —
      // "moves by nothing at all" is a placement too — so it settles rather than
      // starting a motion. The deadband is never consulted while in flight.
      if (!active && Math.abs(next - el.scrollTop) < DEADBAND) {
        settled = null
        onSettled?.()
        return
      }

      const remaining = active ? Math.max(0, duration - (now() - startedAt)) : nextDuration
      // Re-aimed from where the page actually is, so the motion continues from
      // its current position instead of restarting from where it began. What is
      // left of the ease is preserved rather than reset, which is what keeps a
      // superseded target from reading as two movements.
      from = el.scrollTop
      target = next
      startedAt = now()
      duration = nextDuration <= 0
        ? 0
        : active
          ? Math.max(RETARGET_MIN_DURATION, Math.min(nextDuration, remaining))
          : nextDuration
      settled = onSettled ?? null

      if (!active) {
        active = true
        frame = raf(tick)
      }
    },

    abort() {
      active = false
      settled = null
      if (frame !== null) { caf(frame); frame = null }
    },
  }
}
